import React, { useState, useEffect, useRef } from 'react';
import { logoPreloader } from '../services/logoPreloader';

interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl';
  showText?: boolean;
  className?: string;
}

const Logo: React.FC<LogoProps> = ({
  size = 'sm',
  showText = false,
  className = '',
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [usePreloaded, setUsePreloaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
    xl: 'h-20 w-20',
    xxl: 'h-40 w-40',
    xxxl: 'h-50 w-50',
  };

  const textSizes = {
    sm: 'text-lg',
    md: 'text-2xl',
    lg: 'text-3xl',
    xl: 'text-4xl',
    xxl: 'text-5xl',
    xxxl: 'text-6xl',
  };

  useEffect(() => {
    const loadLogo = async () => {
      try {
        // Check if logo is already preloaded
        const preloadedLogo = logoPreloader.getLogo('/images/logo_livetrading_studio_1.svg');
        
        if (preloadedLogo) {
          // Use preloaded logo - render to canvas for instant display
          if (canvasRef.current && preloadedLogo) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              canvas.width = preloadedLogo.naturalWidth;
              canvas.height = preloadedLogo.naturalHeight;
              ctx.drawImage(preloadedLogo, 0, 0);
              setIsLoaded(true);
              setUsePreloaded(true);
            }
          }
        } else {
          // Fallback to regular image loading
          if (imgRef.current) {
            imgRef.current.src = '/images/logo_livetrading_studio_1.svg';
          }
        }
      } catch (error) {
        console.warn('Error loading logo:', error);
        // Fallback to regular image loading
        if (imgRef.current) {
          imgRef.current.src = '/images/logo_livetrading_studio_1.svg';
        }
      }
    };

    loadLogo();
  }, []);

  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <div className={`relative ${sizeClasses[size]}`}>
        <div className="spinnerwrap">
          <div className="outerCircle"></div>
          <div className="innerCircle"></div>
          <div className="icon"></div>
          <div id="diamond-shield"></div>
        </div>

        {/* Canvas for preloaded logos - instant rendering */}
        <canvas
          ref={canvasRef}
          className={`w-full h-full object-contain relative z-10 ${usePreloaded && isLoaded ? 'block' : 'hidden'}`}
        />

        {/* Fallback image element */}
        <img
          ref={imgRef}
          src="/images/logo_livetrading_studio_1.svg"
          alt="Live Trading Studio"
          className={`w-full h-full object-contain relative z-10 ${!usePreloaded || !isLoaded ? 'block' : 'hidden'}`}
          onLoad={handleImageLoad}
          style={{ display: usePreloaded && isLoaded ? 'none' : 'block' }}
        />
      </div>

      {showText && (
        <span className={`font-bold text-gray-100 ${textSizes[size]}`}>
          <img
            src="/images/lts.svg"
            alt="Live Trading Studio"
            className={`w-full h-full object-contain relative z-10`}
          />
          Live Trading Studio
        </span>
      )}
    </div>
  );
};

export default Logo;
